import { useEffect, useRef, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { ArrowRight, BookOpen, Info, X } from 'lucide-react';
import axios from 'axios';
import moment from 'moment';
import { FETCH_USER } from '@/actions/types';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { useIsMobile } from '@/hooks/use-mobile';
import { BottomSheet } from '../mobile';
import { useWalkthroughs } from '../walkthroughs/WalkthroughProvider';
import { canExploreSignupDashboard, type SignupAuth } from './activation';
import { loadVerificationStatus, type VerificationStatusResponse } from './verificationStatusApi';

/** One quiet status bar. Exploring never changes activation or billing. */
export function SignupTextingBanner() {
  const auth = useSelector((state: { auth?: SignupAuth }) => state.auth);
  const location = useLocation();
  const dispatch = useDispatch<any>();
  const walkthroughs = useWalkthroughs();
  const [status, setStatus] = useState<VerificationStatusResponse | null>(null);
  const [refreshError, setRefreshError] = useState('');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const suppressSummaryFocus = useRef(false);
  // Phones: closing the sheet puts focus back on the banner button that opened
  // it, unless the person picked a guide that takes them somewhere else.
  const infoButtonRef = useRef<HTMLButtonElement>(null);
  const returnFocusToInfo = useRef(true);
  const [guidesOpen, setGuidesOpen] = useState(false);
  // Phones show one row (status and the main action); the details and the
  // guides open in a bottom sheet instead of popovers.
  const isMobile = useIsMobile();
  const scope = { businessId: auth?._id || '', userId: auth?.user?._id || '' };
  const owner = auth?.user?.accessLevel === 'owner';
  const preparing = canExploreSignupDashboard(auth);
  const active = auth?.selfServeSignup?.version === 1 && auth.selfServeSignup.status === 'active';
  const staging = auth?.selfServeSignup?.environment === 'staging';
  const eligible = auth?.selfServeSignup?.version === 1 && (preparing || active);
  useEffect(() => {
    if (!eligible) return;
    const controller = new AbortController();
    let running = false;
    const refresh = async () => {
      if (running || document.visibilityState === 'hidden') return;
      running = true;
      try {
        const value = await loadVerificationStatus(scope, controller.signal);
        if (controller.signal.aborted) return;
        setStatus(value);
        setRefreshError('');
        if (value.enabled && value.activationStatus === 'active' && !active) {
          // Refresh actual account permissions only after billing activation. A2P
          // approval by itself must never unlock sending, even in the browser.
          try {
            await dispatch(async (send: any, getState: () => { auth?: SignupAuth }) => {
              const { data } = await axios.get('/api/current_user', { signal: controller.signal, timeout: 15000, _suppressErrorToast: true } as Parameters<typeof axios.get>[1]);
              const current = getState().auth;
              if (!controller.signal.aborted && current?._id === scope.businessId && current?.user?._id === scope.userId &&
                data?._id === scope.businessId && data?.user?._id === scope.userId && data?.user?.active === true &&
                data?.selfServeSignup?.version === 1 && data.selfServeSignup.status === 'active' && !data.reseller) {
                send({ type: FETCH_USER, payload: data });
              }
            });
          } catch { /* Retry account refresh on the next status check. */ }
        }
      } catch {
        if (!controller.signal.aborted) setRefreshError('Status couldn’t refresh. We’ll check again automatically.');
      } finally { running = false; }
    };
    void refresh();
    const timer = window.setInterval(refresh, 30000);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('signup-verification-updated', refresh);
    return () => { controller.abort(); clearInterval(timer); window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', refresh); window.removeEventListener('signup-verification-updated', refresh); };
  }, [scope.businessId, scope.userId, location.pathname, eligible, active, dispatch]);
  const snapshot = status?.enabled && status.businessId === scope.businessId ? status : null;
  const registration = snapshot?.registration;
  const title = registration?.title || (refreshError ? 'Texting status unavailable' : 'Checking texting status…');
  const notStarted = registration?.stage === 'not_started';
  const cancelled = snapshot?.activationStatus === 'cancelled';
  const action = cancelled ? 'Continue setup' : notStarted ? 'Verify business'
    : registration?.stage === 'needs_attention' ? 'Review setup' : 'View status';
  const availableConversation = walkthroughs.guidesEnabled && walkthroughs.data?.availableTourIds.includes('conversation');
  const newToDashboard = availableConversation && walkthroughs.data?.audience.memberCohort === 'new'
    && !walkthroughs.data.progress.welcomeDismissedAt && !walkthroughs.data.progress.tours.conversation?.completedAt;
  const summary = staging ? 'Test mode: no real charges or customer texts. Your number and carrier approval are simulated.'
    : 'Explore contacts, reviews, and templates now. Texting unlocks after approval, number readiness, and your first successful payment.';
  if (!eligible || status?.enabled === false || registration?.complete || (!status && active)) return null;
  const details = <>
    <p className="signup-bar-current-status"><strong>{title}</strong>{registration?.message && <span>{registration.message}</span>}</p>
    {refreshError && <p role="status">{refreshError}</p>}
    {registration?.checkedAt && <p>Last checked {moment(registration.checkedAt).format('lll')}. Updates automatically.</p>}
    <p>Add contacts, prepare templates, connect Google reviews, and get to know your inbox while texting is being set up.</p>
    <h3>Why is verification needed?</h3>
    <p>U.S. carriers require businesses to register before texting U.S. numbers from a local 10-digit number. Called A2P 10DLC, it confirms your business and how customers agree to receive—and stop—your messages. This helps reduce spam and improve delivery.</p>
    <h3>Billing starts when texting is approved and ready.</h3>
    <p>We save your card during setup. Customer texting unlocks after your first payment succeeds.</p>
    {!owner && preparing && <p>Your account owner can finish texting setup.</p>}
    {staging && <div className="signup-bar-test-detail"><strong>Staging test mode</strong><p>No real charges or customer texts. Your test number and carrier approval are simulated.</p></div>}
  </>;
  return <aside className="signup-texting-banner" aria-label="Texting availability">
    <div className="signup-bar-status">
      {staging && <span className="signup-bar-environment">Test mode</span>}
      {isMobile ? <button ref={infoButtonRef} type="button" className="signup-bar-info" aria-haspopup="dialog" aria-expanded={detailsOpen} onClick={() => { returnFocusToInfo.current = true; setDetailsOpen(true); }}>
        <span role="status" aria-live="polite">{title}</span>
        <Info size={16} aria-hidden="true" />
        {newToDashboard && <span className="signup-bar-new-dot" aria-label="New guides available" />}
      </button> : <Popover.Root open={detailsOpen} onOpenChange={open => { setDetailsOpen(open); setSummaryOpen(false); }}>
        <TooltipProvider delayDuration={300}><Tooltip open={summaryOpen && !detailsOpen && !guidesOpen} onOpenChange={open => setSummaryOpen(open && !detailsOpen && !guidesOpen)}>
          <TooltipTrigger asChild><Popover.Trigger asChild>
            <button className="signup-bar-info" aria-label="About texting setup"
              onFocus={event => {
                if (suppressSummaryFocus.current || !event.currentTarget.matches(':focus-visible')) event.preventDefault();
              }}><span role="status" aria-live="polite">{title}</span><Info size={16} aria-hidden="true" /></button>
          </Popover.Trigger></TooltipTrigger>
          {!detailsOpen && <TooltipContent className="signup-bar-tooltip" side="bottom" sideOffset={12}>{registration?.message && <strong>{registration.message} </strong>}{summary}<span>Click for details.</span></TooltipContent>}
        </Tooltip></TooltipProvider>
        <Popover.Portal><Popover.Content className="signup-bar-panel" sideOffset={12} collisionPadding={16} align="start" aria-label="Texting setup details" onCloseAutoFocus={() => {
          // Suppress only the popover's synchronous focus return, not the next
          // deliberate keyboard visit (or focus after an outside click).
          suppressSummaryFocus.current = true;
          setSummaryOpen(false);
          queueMicrotask(() => { suppressSummaryFocus.current = false; });
        }}>
          <div className="signup-bar-panel-heading"><h2>Keep exploring your dashboard.</h2><Popover.Close aria-label="Close explanation"><X size={18} /></Popover.Close></div>
          {details}
        </Popover.Content></Popover.Portal>
      </Popover.Root>}
      <span className="signup-bar-caption">{refreshError ? 'We’ll try again shortly.' : 'Explore your dashboard now.'}</span>
    </div>
    <div className="signup-bar-actions">
      {walkthroughs.guidesEnabled && !isMobile && <Popover.Root open={guidesOpen} onOpenChange={open => { setGuidesOpen(open); setSummaryOpen(false); }}>
        <Popover.Trigger asChild><button className="signup-bar-guides"><BookOpen size={16} aria-hidden="true" />Guides{newToDashboard && <span className="signup-bar-new-dot" aria-label="New guides available" />}</button></Popover.Trigger>
        <Popover.Portal><Popover.Content className="signup-bar-panel signup-bar-guide-panel" sideOffset={12} collisionPadding={16} align="end" aria-label="Dashboard guides">
          <div className="signup-bar-panel-heading"><h2>Make yourself at home.</h2><Popover.Close aria-label="Close guides"><X size={18} /></Popover.Close></div>
          <p>Short guides, right in your dashboard. Explore at your own pace.</p>
          {availableConversation && <button className="signup-guide-start" disabled={walkthroughs.busy || !!walkthroughs.guide} onClick={async () => { if (await walkthroughs.start('conversation')) setGuidesOpen(false); }}>Show me the inbox<ArrowRight size={16} /></button>}
          <Link className="signup-guide-browse" to="/walkthroughs" onClick={() => setGuidesOpen(false)}>Browse walkthroughs<ArrowRight size={16} /></Link>
          {newToDashboard && <button className="signup-guide-dismiss" disabled={walkthroughs.busy} onClick={() => { walkthroughs.dismissWelcome(); setGuidesOpen(false); }}>Not now</button>}
          {walkthroughs.error && <p role="alert">{walkthroughs.error}</p>}
        </Popover.Content></Popover.Portal>
      </Popover.Root>}
      {owner && snapshot?.canManage && (active
        ? <a className="signup-bar-primary" href="mailto:support@chekkit.io">Contact support<ArrowRight size={16} /></a>
        : <Link className="signup-bar-primary" aria-label={action === 'Verify business' ? 'Verify your business for texting' : action} to={notStarted && !cancelled ? '/onboarding?verify=1' : '/onboarding'}>{action}<ArrowRight size={16} /></Link>)}
    </div>
    {isMobile && <BottomSheet open={detailsOpen} onOpenChange={setDetailsOpen} title="Keep exploring your dashboard." onCloseAutoFocus={event => {
      // After 'Show me the inbox' or 'Browse walkthroughs', leave focus where the guide or page puts it.
      event.preventDefault();
      if (returnFocusToInfo.current && infoButtonRef.current?.isConnected) infoButtonRef.current.focus();
    }}>
      <div className="signup-bar-sheet">
        {details}
        {walkthroughs.guidesEnabled && <section aria-label="Dashboard guides">
          <h3>Make yourself at home.</h3>
          <p>Short guides, right in your dashboard. Explore at your own pace.</p>
          {availableConversation && <button type="button" className="signup-sheet-guide-start" disabled={walkthroughs.busy || !!walkthroughs.guide} onClick={async () => { returnFocusToInfo.current = false; setDetailsOpen(false); await walkthroughs.start('conversation'); }}>Show me the inbox<ArrowRight size={16} /></button>}
          <Link className="signup-sheet-guide-browse" to="/walkthroughs" onClick={() => { returnFocusToInfo.current = false; setDetailsOpen(false); }}>Browse walkthroughs<ArrowRight size={16} /></Link>
          {newToDashboard && <button type="button" className="signup-sheet-guide-browse" disabled={walkthroughs.busy} onClick={() => { walkthroughs.dismissWelcome(); setDetailsOpen(false); }}>Not now</button>}
          {walkthroughs.error && <p role="alert">{walkthroughs.error}</p>}
        </section>}
      </div>
    </BottomSheet>}
  </aside>;
}
